import Service, { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { isPresent } from '@ember/utils';
import { waitForPromise } from '@ember/test-waiters';
import { TrackedMap } from 'tracked-built-ins';
import { addDays } from 'date-fns/addDays';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { parseISO } from 'date-fns/parseISO';
import BookingModel from 'uplisting-frontend/models/booking';
import MultiUnitModel from 'uplisting-frontend/models/multi-unit';
import { occasionPriceOf } from 'uplisting-frontend/models/price';
import { formatLocalDate } from 'uplisting-frontend/utils/date';
import {
  clashesOf,
  locationOf,
} from 'uplisting-frontend/services/calendar/-internals/occupancy';
import type { IExtraPriceSchema } from 'uplisting-frontend/models/extra-price';
import type { IQuoteToPrice } from 'uplisting-frontend/models/quote';
import type { OccasionRecord } from 'uplisting-frontend/services/calendar/-internals/record';
import type { ConflictRecord } from 'uplisting-frontend/services/calendar/-internals/conflict';
import type {
  LocationType,
  PropertyRowDataItem,
} from 'uplisting-frontend/services/calendar/-internals/types';
import type { IUnitMove } from 'uplisting-frontend/services/calendar/unit-move';
import type { ModelRegistry } from 'ember-data/types/registries/model';

type Booking = ModelRegistry['booking'];

interface IStay {
  multiUnit?: ModelRegistry['multi-unit'];
  checkIn: string;
  checkOut: string;
}

interface IRequote {
  price: IQuoteToPrice;
  charges: IExtraPriceSchema[];
  discounts: IExtraPriceSchema[];
}

export interface IOverlap {
  booking: Booking;
  location: LocationType;
  others: PropertyRowDataItem[];
}

function stayNow(booking: Booking): IStay {
  return {
    multiUnit: booking.multiUnit,
    checkIn: booking.checkIn,
    checkOut: booking.checkOut,
  };
}

export class Placement {
  @tracked saved: IStay;
  @tracked quote?: IRequote;
  @tracked quoteFailed = false;

  booking: Booking;

  constructor(booking: Booking) {
    this.booking = booking;
    this.saved = stayNow(booking);
  }

  get unitChanged(): boolean {
    return this.booking.multiUnit !== this.saved.multiUnit;
  }

  get datesChanged(): boolean {
    const { checkIn, checkOut } = this.booking;

    return checkIn !== this.saved.checkIn || checkOut !== this.saved.checkOut;
  }

  get hasChanges(): boolean {
    return this.unitChanged || this.datesChanged;
  }

  get isQuoting(): boolean {
    return this.datesChanged && !this.quote && !this.quoteFailed;
  }

  get isPriced(): boolean {
    return !this.datesChanged || !!this.quote;
  }

  get quotedPrice(): number | null | undefined {
    return this.quote && occasionPriceOf(this.quote.price);
  }
}

export default class CalendarConflictResolutionService extends Service {
  @service store!: Services['store'];
  @service notifications!: Services['notifications'];
  @service('calendar/data') calendarData!: Services['calendar/data'];
  @service('calendar/unit-move') unitMove!: Services['calendar/unit-move'];

  @service('repositories/price')
  priceRepository!: Services['repositories/price'];

  @tracked conflict?: ConflictRecord;
  @tracked records: OccasionRecord[] = [];
  @tracked lastPlaced?: Booking;
  @tracked isResolving = false;

  #placements = new TrackedMap<Booking, Placement>();

  #cancelOnceSaved = false;

  get isActive(): boolean {
    return !!this.conflict;
  }

  get placements(): Placement[] {
    return [...this.#placements.values()];
  }

  get isDirty(): boolean {
    return this.placements.some((placement) => placement.hasChanges);
  }

  @cached
  get clashes(): Map<PropertyRowDataItem, PropertyRowDataItem[]> {
    return new Map(this.records.map(({ model }) => [model, clashesOf(model)]));
  }

  get heldRecords(): OccasionRecord[] {
    return this.records.filter((record) => this.isHeld(record.model));
  }

  @cached
  get overlaps(): IOverlap[] {
    const bookings = this.records
      .map((record) => record.model)
      .filter((model): model is Booking => model instanceof BookingModel);

    return bookings.flatMap((booking, index) => {
      const reported = bookings.slice(0, index);

      const others = (this.clashes.get(booking) ?? []).filter(
        (other) => !reported.includes(other as Booking),
      );

      return others.length
        ? [{ booking, location: locationOf(booking), others }]
        : [];
    });
  }

  get canResolve(): boolean {
    return (
      !this.isResolving &&
      this.isDirty &&
      this.placements.every((placement) => placement.isPriced) &&
      this.overlaps.length === 0
    );
  }

  public start(conflict: ConflictRecord): boolean {
    if (this.conflict === conflict || this.keepsArrangement()) {
      return false;
    }

    this.#discard();
    this.conflict = conflict;

    this.#refresh();

    return true;
  }

  public keepsArrangement(): boolean {
    if (!this.isDirty) {
      return false;
    }

    this.notifications.error(
      'calendar.conflict_resolution.notifications.unresolved',
    );

    return true;
  }

  public cancel(): void {
    if (!this.conflict) {
      return;
    }

    if (this.isResolving) {
      this.#cancelOnceSaved = true;

      return;
    }

    this.#discard();

    this.calendarData.setupFasterLookup();
  }

  public canMove(model: PropertyRowDataItem): boolean {
    if (!this.isActive) {
      return true;
    }

    const property = this.conflict?.records[0]?.model.property;

    return (
      model instanceof BookingModel &&
      !this.isResolving &&
      model.property === property
    );
  }

  public isPlaced(model: PropertyRowDataItem): boolean {
    return model instanceof BookingModel && this.#placements.has(model);
  }

  public isHeld(model: PropertyRowDataItem): boolean {
    return model !== this.lastPlaced && !!this.clashes.get(model)?.length;
  }

  public placementOf(booking: Booking): Placement | undefined {
    return this.#placements.get(booking);
  }

  public place(
    record: OccasionRecord,
    location: LocationType,
    checkIn: string,
  ): void {
    const booking = record.model as Booking;

    if (!this.canMove(booking)) {
      return;
    }

    this.#move(booking, location, checkIn);
    this.lastPlaced = booking;

    this.#refresh();
  }

  public placeOnUnit({ booking, from, to, swapWith }: IUnitMove): void {
    if (!this.canMove(booking)) {
      return;
    }

    this.#move(booking, to, booking.checkIn);

    if (swapWith) {
      this.#move(swapWith, from, swapWith.checkIn);
    }

    this.lastPlaced = booking;

    this.#refresh();
  }

  public async resolve(): Promise<boolean> {
    if (!this.canResolve) {
      return false;
    }

    this.isResolving = true;

    try {
      for (const placement of this.placements) {
        if (!placement.hasChanges) {
          continue;
        }

        try {
          await this.#save(placement);
        } catch {
          this.notifications.error(
            'calendar.conflict_resolution.notifications.save_failed',
            false,
            { guest: placement.booking.displayGuestName },
          );

          return false;
        }
      }

      this.notifications.info(
        'calendar.conflict_resolution.notifications.resolved',
      );

      this.#reset();
      this.calendarData.setupFasterLookup();

      return true;
    } finally {
      this.isResolving = false;

      if (this.#cancelOnceSaved) {
        this.cancel();
      }
    }
  }

  #move(booking: Booking, location: LocationType, checkIn: string): void {
    let placement = this.#placements.get(booking);

    if (!placement) {
      placement = new Placement(booking);

      this.#placements.set(booking, placement);
    }

    if (location instanceof MultiUnitModel) {
      booking.multiUnit = location;
    }

    if (checkIn !== booking.checkIn) {
      const nights = differenceInCalendarDays(
        parseISO(booking.checkOut),
        parseISO(booking.checkIn),
      );

      booking.checkIn = checkIn;
      booking.checkOut = formatLocalDate(addDays(parseISO(checkIn), nights));

      this.#requote(placement);
    }
  }

  #refresh(): void {
    this.#refreshRecords();

    this.calendarData.setupFasterLookup();
  }

  #refreshRecords(): void {
    const records = [...(this.conflict?.records ?? [])];

    const add = (model: PropertyRowDataItem): void => {
      if (!records.some((record) => record.model === model)) {
        records.push(this.#recordFor(model));
      }
    };

    const moved = this.placements.map(({ booking }) => booking);

    moved.forEach(add);
    moved.flatMap((booking) => clashesOf(booking)).forEach(add);

    this.records = records;
  }

  #recordFor(model: PropertyRowDataItem): OccasionRecord {
    return (
      this.records.find((record) => record.model === model) ??
      this.calendarData.recordFor(model)
    );
  }

  #discard(): void {
    for (const placement of this.placements) {
      this.#restore(placement);
    }

    this.#reset();
  }

  #reset(): void {
    this.conflict = undefined;
    this.records = [];
    this.#placements.clear();
    this.lastPlaced = undefined;
    this.#cancelOnceSaved = false;
  }

  #restore({ booking, saved }: Placement): void {
    booking.rollbackAttributes();
    booking.multiUnit = saved.multiUnit;
  }

  #requote(placement: Placement): void {
    placement.quote = undefined;
    placement.quoteFailed = false;

    if (placement.datesChanged) {
      void waitForPromise(this.#quote(placement));
    }
  }

  async #quote(placement: Placement): Promise<void> {
    const { booking } = placement;
    const { price, checkIn, checkOut } = booking;

    if (!price) {
      placement.quoteFailed = true;

      return;
    }

    const quote = this.store.createRecord('quote', {
      booking,
      property: booking.property,
      checkIn,
      checkOut,
      charges: price.charges.map((item) => item.toObject()),
      discounts: price.discounts.map((item) => item.toObject()),
      cleaningFee: price.cleaningFee,
      numberOfGuests: price.numberOfGuests,
    });

    const requote = await quote.save().then(
      (): IRequote => ({
        price: quote.toObject(),
        charges: quote.charges,
        discounts: quote.discounts,
      }),
      () => undefined,
    );

    this.store.unloadRecord(quote);

    if (booking.checkIn !== checkIn || booking.checkOut !== checkOut) {
      return;
    }

    if (requote) {
      placement.quote = requote;
    } else {
      placement.quoteFailed = true;
    }
  }

  async #save(placement: Placement): Promise<void> {
    const { booking, quote } = placement;
    const { multiUnit, checkIn, checkOut } = booking;

    if (placement.unitChanged && multiUnit) {
      await this.unitMove.saveUnit(booking, multiUnit);

      placement.saved = { ...placement.saved, multiUnit };

      booking.checkIn = checkIn;
      booking.checkOut = checkOut;
    }

    if (placement.datesChanged && quote) {
      await this.#savePrice(booking, quote);

      placement.saved = { ...placement.saved, checkIn, checkOut };
    }
  }

  async #savePrice(booking: Booking, quote: IRequote): Promise<void> {
    const { price } = booking;
    const { charges, discounts } = price;

    price.setProperties({
      ...quote.price,
      checkIn: booking.checkIn,
      checkOut: booking.checkOut,
    });

    price.charges = this.#toExtraPrices(quote.charges);
    price.discounts = this.#toExtraPrices(quote.discounts);

    try {
      await this.priceRepository.save(price);
    } catch (error) {
      price.dropOrphanedExtraPrices();
      price.rollbackAttributes();
      price.charges = charges;
      price.discounts = discounts;

      throw error;
    }

    price.dropOrphanedExtraPrices();

    await booking.reload();
  }

  #toExtraPrices(items: IExtraPriceSchema[]): ModelRegistry['extra-price'][] {
    return items
      .filter((item) => isPresent(item.amount))
      .map((item) => this.store.createRecord('extra-price', item));
  }
}

declare module '@ember/service' {
  interface Registry {
    'calendar/conflict-resolution': CalendarConflictResolutionService;
  }
}
